export const SUPPORTED_LOCALES = ['bg', 'en', 'tr', 'de', 'sv'] as const

export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: AppLocale = 'bg'

/** `hreflang="x-default"` (primary market / SEO focus: Bulgaria). */
export const HREFLANG_X_DEFAULT_LOCALE: AppLocale = 'bg'

/** Persisted UI language (used when URL has no locale segment, e.g. `/`). */
export const LOCALE_STORAGE_KEY = 'eventatv_app_locale_v1'

export function isAppLocale(value: string | undefined): value is AppLocale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

export function readCachedLocale(): AppLocale | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    if (raw && isAppLocale(raw)) return raw
  } catch {
    /* ignore quota / private mode */
  }
  return null
}

export function writeCachedLocale(locale: AppLocale) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    /* ignore */
  }
}

/** First visit or cleared storage: browser languages; then cache. */
export function resolveInitialLocale(): AppLocale {
  return readCachedLocale() ?? detectBrowserLocale()
}

export function detectBrowserLocale(): AppLocale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE
  const list = navigator.languages?.length
    ? navigator.languages
    : [navigator.language]
  for (const raw of list) {
    const base = raw.split('-')[0].toLowerCase()
    if (isAppLocale(base)) return base
  }
  return DEFAULT_LOCALE
}

export const LOCALE_LABELS: Record<AppLocale, string> = {
  bg: 'Български',
  en: 'English',
  tr: 'Türkçe',
  de: 'Deutsch',
  sv: 'Svenska',
}

/** Flag emoji for language UI (may show as regional letters on some Windows builds). */
export const LOCALE_FLAGS: Record<AppLocale, string> = {
  bg: '🇧🇬',
  en: '🇬🇧',
  tr: '🇹🇷',
  de: '🇩🇪',
  sv: '🇸🇪',
}

/** BCP 47 tag for `toLocaleDateString` / number formatting. */
export function appLocaleToBcp47(locale: AppLocale): string {
  switch (locale) {
    case 'bg':
      return 'bg-BG'
    case 'en':
      return 'en-GB'
    case 'tr':
      return 'tr-TR'
    case 'de':
      return 'de-DE'
    case 'sv':
      return 'sv-SE'
    default:
      return 'en-GB'
  }
}
